import React, { useCallback, useMemo } from 'react';
import { Avatar, Chip, Spinner } from '@heroui/react';
import { Icon } from '@iconify/react';
import { UniversalTable, IdView, Typography } from '../../../../components';
import { EmptyContentIc, MoneyIc } from '../../../../components/icons';
import { useTranslations } from '../../../../contexts/translation.context';
import { PaginationInfo, TableColumn } from '../../../../types/account';
import { RefShareCost, CostStatus, OwnerLevel } from '../../../../api/services/refShareApi';

export type { RefShareCost as RevshareBill };

type RevshareBillsTableProps = {
  bills: RefShareCost[];
  pagination: PaginationInfo;
  isLoading?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  sortBy: string;
  onSortChange: (sortKey: string) => void;
};

const statusConfig: Record<CostStatus, { dotColor: string; label: string }> = {
  in_pending: { dotColor: 'bg-[#006FEE]', label: 'in_pending' },
  paid: { dotColor: 'bg-[#17C964]', label: 'paid' },
};

// Level badge configuration
const getLevelBadge = (level?: OwnerLevel) => {
  if (!level) return null;

  switch (level.toLowerCase()) {
    case 'new':
      return { label: 'New', bg: 'rgba(23,201,100,0.15)', color: '#17c964' };
    case 'base':
      return { label: 'Base', bg: '#F4F4F5', color: '#71717A' };
    case 'pro':
      return { label: 'PRO', bg: '#11181C', color: '#FFFFFF' };
    case 'expert':
      return { label: 'Expert', bg: 'rgba(120, 40, 200, 0.2)', color: '#7828c8' };
    case 'bronze':
      return { label: 'Bronze', bg: 'rgba(205, 127, 50, 0.2)', color: '#CD7F32' };
    case 'silver':
      return { label: 'Silver', bg: 'rgba(192, 192, 192, 0.3)', color: '#71717A' };
    case 'gold':
      return { label: 'Gold', bg: 'rgba(255, 215, 0, 0.2)', color: '#B8860B' };
    case 'platinum':
      return { label: 'Platinum', bg: 'rgba(229, 228, 226, 0.3)', color: '#71717A' };
    case 'diamond':
      return { label: 'Diamond', bg: 'rgba(185, 242, 255, 0.3)', color: '#00CED1' };
    default:
      return { label: level, bg: '#F4F4F5', color: '#71717A' };
  }
};

const columns: TableColumn[] = [
  { key: 'id', name: 'ID' },
  { key: 'customer', name: 'Заказчик' },
  { key: 'payer', name: 'Плательщик' },
  { key: 'account', name: 'Аккаунт' },
  { key: 'amount', name: 'Сумма' },
  { key: 'status', name: 'Статус' },
  { key: 'date', name: 'Дата', sortKey: 'created_at', sortable: true },
];

export const RevshareBillsTable: React.FC<RevshareBillsTableProps> = ({
  bills,
  pagination,
  isLoading,
  onPageChange,
  onPageSizeChange,
  sortBy,
  onSortChange,
}) => {
  const { t } = useTranslations();

  const statusLabels = useMemo(
    () => ({
      in_pending: t('admin.revshareBills.status.in_pending'),
      paid: t('admin.revshareBills.status.paid'),
    }),
    [t]
  );

  const getAmount = (amount: string | number): number => {
    if (typeof amount === 'string') {
      return parseFloat(amount) || 0;
    }
    return amount;
  };

  const formatDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const renderCell = useCallback((bill: RefShareCost, columnKey: string) => {
    switch (columnKey) {
      case 'id': {
        const displayId = bill._id.slice(-4);
        return (
          <IdView textToCopy={bill._id}>
            {displayId}
          </IdView>
        );
      }
      case 'customer': {
        // Partner is the marketer who issued the bill (Заказчик)
        const partner = bill.partner;
        if (!partner) return null;

        const badge = getLevelBadge(partner.lvl);

        return (
          <div className="flex items-center gap-[8px]">
            <Avatar
              src={partner.avatar}
              showFallback
              name={partner.name?.slice(0, 1)}
              className="w-[32px] h-[32px] rounded-[8px] flex-shrink-0"
            />
            <div className="flex flex-col gap-[2px]">
              <a
                href={`/users/partners/${partner._id}`}
                className="text-[#006FEE] font-semibold text-[14px] hover:underline"
              >
                {partner.name || 'Unknown'}
              </a>
              {badge && (
                <Chip
                  classNames={{
                    base: 'h-[20px] px-[8px]',
                    content: 'text-[11px] font-semibold'
                  }}
                  style={{ backgroundColor: badge.bg, color: badge.color }}
                >
                  {badge.label}
                </Chip>
              )}
            </div>
          </div>
        );
      }
      case 'payer': {
        // Client is the one who needs to pay (Плательщик)
        const client = bill.client;
        if (!client) return null;

        const levelBadge = getLevelBadge(client.lvl);
        // Determine URL based on client type
        const href = client.type === 'agency'
          ? `/users/partners/${client._id}`
          : `/users/creators/${client._id}`;

        // Type badge styling
        const typeBadge = {
          label: client.type === 'agency' ? t('admin.creators.type.agency') : t('admin.creators.type.creator'),
          bg: client.type === 'agency' ? 'rgba(120, 40, 200, 0.13)' : 'rgba(0, 111, 238, 0.13)',
          color: client.type === 'agency' ? '#7828C8' : '#006FEE'
        };

        return (
          <div className="flex items-center gap-[8px]">
            <Avatar
              src={client.avatar}
              showFallback
              name={client.name?.slice(0, 1)}
              className="w-[32px] h-[32px] rounded-[8px] flex-shrink-0"
            />
            <div className="flex flex-col gap-[2px]">
              <a
                href={href}
                className="text-[#006FEE] font-semibold text-[14px] hover:underline"
              >
                {client.name || 'Unknown'}
              </a>
              <div className="flex items-center gap-[4px]">
                {levelBadge && (
                  <Chip
                    classNames={{
                      base: 'h-[20px] px-[8px]',
                      content: 'text-[11px] font-semibold'
                    }}
                    style={{ backgroundColor: levelBadge.bg, color: levelBadge.color }}
                  >
                    {levelBadge.label}
                  </Chip>
                )}
                <Chip
                  classNames={{
                    base: 'h-[20px] px-[8px]',
                    content: 'text-[11px] font-semibold'
                  }}
                  style={{ backgroundColor: typeBadge.bg, color: typeBadge.color }}
                >
                  {typeBadge.label}
                </Chip>
              </div>
            </div>
          </div>
        );
      }
      case 'account': {
        const modelName = bill.model?.name || bill.model?.of_user_name || '';
        const modelUsername = bill.model?.of_user_name || '';
        const popularityPercent = bill.model?.overall_popularity_percent;

        return (
          <div className="flex items-start gap-[12px]">
            <Avatar
              src={bill.model?.avatar}
              showFallback
              name={modelName?.slice(0, 1)}
              className="w-[40px] h-[40px] rounded-[12px] flex-shrink-0"
            />
            <div className="flex flex-col">
              <div className="flex items-center gap-[8px]">
                <Typography variant="body1" className="text-[#11181C] font-semibold">
                  {modelName}
                </Typography>
                {modelUsername && (
                  <a
                    href={`https://onlyfans.com/${modelUsername}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#006FEE] font-semibold text-[14px] hover:underline"
                  >
                    @{modelUsername}
                  </a>
                )}
              </div>
              {popularityPercent !== undefined && popularityPercent !== null && (
                <div className="flex flex-wrap gap-[8px] mt-[4px]">
                  <Chip
                    classNames={{
                      base: 'h-[20px] px-[6px] bg-[#F5A524]/20',
                      content: 'text-[#F5A524] text-[12px]'
                    }}
                  >
                    {popularityPercent.toFixed(1)}%
                  </Chip>
                </div>
              )}
            </div>
          </div>
        );
      }
      case 'amount':
        return (
          <div className="flex items-center gap-[8px] text-[#11181C] font-semibold">
            <MoneyIc size={18} />
            ${getAmount(bill.amount).toFixed(2)}
          </div>
        );
      case 'status': {
        const cfg = statusConfig[bill.status];
        return (
          <Chip
            classNames={{
              base: 'h-[24px] px-[8px] py-[4px] gap-[4px] bg-gray-100',
              content: 'text-gray-900 text-[12px]'
            }}
            startContent={
              <div className={`w-2 h-2 rounded-full ${cfg?.dotColor || 'bg-gray-600'}`} />
            }
          >
            {statusLabels[bill.status] || bill.status}
          </Chip>
        );
      }
      case 'date':
        return (
          <div className="flex items-center gap-[8px] text-[14px] text-[#11181C]">
            <Icon icon="solar:calendar-linear" fontSize={18} className="text-[#A1A1AA]" />
            {formatDate(bill.created_at)}
          </div>
        );
      default:
        return null;
    }
  }, [statusLabels, t]);

  const emptyContent = isLoading ? (
    <div className="flex flex-col items-center justify-center py-[48px] gap-[12px]">
      <Spinner color="primary" />
      <Typography variant="body2" className="text-[#71717A]">
        {t('common.loading')}
      </Typography>
    </div>
  ) : (
    <div className="flex flex-col items-center justify-center py-[48px] gap-[12px]">
      <EmptyContentIc />
      <Typography variant="body1" className="font-semibold">
        {t('admin.revshareBills.table.empty.title')}
      </Typography>
      <Typography variant="body3" className="text-[#71717A]">
        {t('admin.revshareBills.table.empty.description')}
      </Typography>
    </div>
  );

  return (
    <UniversalTable
      data={bills}
      columns={columns}
      renderCell={renderCell}
      emptyContent={emptyContent}
      pagination={pagination}
      currentPage={pagination.page}
      currentPageSize={pagination.page_size}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      isSearchEmpty={!bills.length}
      sortBy={sortBy}
      onSort={onSortChange}
    />
  );
};
